import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

const BASE_URL = 'cancerigenos';
export interface NewCarcinogenicReport {
    idEstablecimiento: number;
    establecimiento: {
        propiosAdministrativos: number;
        eventualesAdministrativos: number;
        propiosProduccion: number;
        eventualesProduccion: number;
        permiteFumarLugarCerrado: boolean;
        permiteFumar: boolean;
        observacion: string;
    };
}

export interface ForcedWrongNewCarcinogenicReport {
    idEstablecimiento: number;
    establecimiento: {
        propiosAdministrativos: null;
        eventualesAdministrativos: null;
        propiosProduccion: null;
        eventualesProduccion: null;
        permiteFumarLugarCerrado: null;
        permiteFumar: null;
        observacion: null;
    };
}

export interface EditSubstanceBody {
    nombreComercial: string;
    usos: number[];
    modosEmpleo: number[];
    informaRiesgo: boolean;
    capacitaRiesgo: boolean;
    elementosProteccion: number[];
    medidasPrevencion: number[];
    estudiosRemplazo: string;
    licenciaEspecial: boolean;
    cantidadAnual?: number | null;
    idUnidadMedida?: number | null;
}

interface SubstancesParams {
    idEstablecimiento: number;
    skip: number;
    take: number;
}

export interface NewSubstanceStudyBody {
    idTipo: number;
    idTipoFrecuencia: number;
    detalle: string;
    cantidad: number;
}

export interface NewProviderBody {
    nombreQuimico: string;
    cuit: string;
    tipo: {};
}

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});
export interface NewResponsibleBody {
    nombre: string;
    apellido: string;
    cuit: string;
    titulo: string;
    matricula: string;
    entidadOtorgante: string;
    idCargo: number;
    idTipoContratacion: number;
    idRepresentacion: number;
    idTipoProfesional: number;
    cantidadHoras: number;
}
export interface NewContractorBody {
    cuit: string;
    idActividad: number;
    tipoEmpresa: { id: number; codigo: string; descripcion: string };
    cantidadTrabajadores: number;
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const { auth }: any = api.getState();
        const refreshResult = await baseQuery(
            {
                url: '/auth/refresh-token',
                method: 'POST',
                credentials: 'include',
                body: { username: auth?.user?.usuario }
            },
            api,
            extraOptions
        );
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const carcinogenicApi = createApi({
    reducerPath: 'carcinogenic',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'CarcinogenicParametricData',
        'Substances',
        'CarcinogenicReport',
        'CarcinogenicReportWorkers',
        'CarcinogenicReportWorkerDetail'
    ],
    endpoints: (builder) => ({
        createCarcinogenicReport: builder.mutation({
            query: (body: NewCarcinogenicReport | ForcedWrongNewCarcinogenicReport) => ({
                url: `${BASE_URL}`,
                body: body,
                method: 'POST'
            })
        }),
        editCarcinogenicReportEstablishment: builder.mutation({
            query: ({
                reportId,
                body,
                isBorrador
            }: {
                reportId: number;
                body: NewCarcinogenicReport;
                isBorrador?: boolean;
            }) => {
                const { idEstablecimiento, ...rest } = body;
                return {
                    url: `${BASE_URL}/${reportId}`,
                    body: { ...rest, esBorrador: isBorrador === false ? false : true },
                    method: 'PATCH'
                };
            }
        }),
        getCarcinogenicParametricData: builder.query({
            query: (params) => ({
                url: `${BASE_URL}/datos-parametricos`,
                params
            }),
            providesTags: ['CarcinogenicParametricData']
        }),
        getCarcinogenicReport: builder.query({
            query: (reportId: number | null) => {
                return {
                    url: `${BASE_URL}/${reportId}`
                };
            },
            providesTags: ['CarcinogenicReport']
        }),
        getSubstances: builder.query({
            query: (params: SubstancesParams) => ({
                url: `${BASE_URL}/sustancias`,
                params
            }),
            providesTags: ['Substances']
        }),
        editCarcinogenicReport: builder.mutation({
            query: ({ body, reportId }: { body: NewCarcinogenicReport; reportId: number }) => ({
                url: `${BASE_URL}/sustancias/${reportId}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicSubstance: builder.mutation({
            query: ({
                body,
                reportId,
                substanceId
            }: {
                body: EditSubstanceBody;
                reportId: number;
                substanceId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        createCarcinogenicSubstanceReport: builder.mutation({
            query: ({
                body,
                reportId,
                substanceId
            }: {
                body: NewCarcinogenicReport;
                reportId: number;
                substanceId: number;
            }) => ({
                url: `${BASE_URL}/sustancias/${reportId}/sustancias/${substanceId}/estudios`,
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicSubstanceStudy: builder.mutation({
            query: ({
                reportId,
                substanceId,
                studyId
            }: {
                reportId: number;
                substanceId: number;
                studyId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/estudios/${studyId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicSubstanceStudy: builder.mutation({
            query: ({
                reportId,
                substanceId,
                studyId
            }: {
                reportId: number;
                substanceId: number;
                studyId: number;
            }) => ({
                url: `${BASE_URL}/sustancias/${reportId}/sustancias/${substanceId}/estudios/${studyId}`,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        createCarcinogenicSubstanceStudy: builder.mutation({
            query: ({
                reportId,
                substanceId,
                body
            }: {
                reportId: number;
                substanceId: number;
                body: NewSubstanceStudyBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/estudios`,
                body: body,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        createCarcinogenicProvider: builder.mutation({
            query: ({
                reportId,
                substanceId,
                body
            }: {
                reportId: number;
                substanceId: number;
                body: NewProviderBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/proveedores`,
                body: body,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicProvider: builder.mutation({
            query: ({
                reportId,
                substanceId,
                providerId,
                body
            }: {
                reportId: number;
                substanceId: number;
                providerId: number;
                body: NewProviderBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/proveedores/${providerId}`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicProvider: builder.mutation({
            query: ({
                reportId,
                substanceId,
                providerId
            }: {
                reportId: number;
                substanceId: number;
                providerId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/proveedores/${providerId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        createRadiologicalEquipment: builder.mutation({
            query: ({
                reportId,
                substanceId,
                body
            }: {
                reportId: number;
                substanceId: number;
                body: any;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/equipos`,
                body: body,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicEquipment: builder.mutation({
            query: ({
                reportId,
                substanceId,
                equipmentId,
                body
            }: {
                reportId: number;
                substanceId: number;
                equipmentId: number;
                body: any;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/equipos/${equipmentId}`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicEquipment: builder.mutation({
            query: ({
                reportId,
                substanceId,
                equipmentId
            }: {
                reportId: number;
                substanceId: number;
                equipmentId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/equipos/${equipmentId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicEquipmentResponsible: builder.mutation({
            query: ({
                reportId,
                substanceId,
                equipmentId,
                responsibleId
            }: {
                reportId: number;
                substanceId: number;
                equipmentId: number;
                responsibleId: number;
            }) => ({
                url: `${BASE_URL}/${reportId}/sustancias/${substanceId}/equipos/${equipmentId}/responsables/${responsibleId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        getCarcinogenicReportWorkers: builder.query({
            query: ({ reportId, filters }) => {
                return {
                    url: `${BASE_URL}/${reportId}/trabajadores`,
                    params: filters
                };
            },
            providesTags: ['CarcinogenicReportWorkers']
        }),
        getCarcinogenicReportWorkerDetail: builder.query({
            query: ({ reportId, wokerId }) => {
                return {
                    url: `${BASE_URL}/${reportId}/trabajadores/${wokerId}`
                };
            },
            providesTags: ['CarcinogenicReportWorkers']
        }),
        editCarcinogenicReportWorkerDetail: builder.mutation({
            query: ({ reportId, workerId, body }) => {
                return {
                    url: `${BASE_URL}/${reportId}/trabajadores/${workerId}`,
                    body: body,
                    method: 'PATCH'
                };
            },
            invalidatesTags: [
                'CarcinogenicReportWorkerDetail',
                'CarcinogenicReportWorkers',
                'CarcinogenicReport'
            ]
        }),
        addCarcinogenicReportContractor: builder.mutation({
            query: ({ reportId, body }: { reportId: number; body: NewContractorBody }) => ({
                url: `${BASE_URL}/${reportId}/contratistas`,
                body: { ...body, tipoEmpresa: body?.tipoEmpresa?.id },
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicReportContractor: builder.mutation({
            query: ({
                reportId,
                contractorId,
                body
            }: {
                reportId: number;
                contractorId: number;
                body: NewContractorBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/contratistas/${contractorId}`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicContractor: builder.mutation({
            query: ({ reportId, contractorId }: { reportId: number; contractorId: number }) => ({
                url: `${BASE_URL}/${reportId}/contratistas/${contractorId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        addCarcinogenicReportResponsible: builder.mutation({
            query: ({ reportId, body }: { reportId: number; body: NewResponsibleBody }) => ({
                url: `${BASE_URL}/${reportId}/responsables`,
                body: body,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        editCarcinogenicReportResponsible: builder.mutation({
            query: ({
                reportId,
                responsibleId,
                body
            }: {
                reportId: number;
                responsibleId: number;
                body: NewResponsibleBody;
            }) => ({
                url: `${BASE_URL}/${reportId}/responsables/${responsibleId}`,
                body: body,
                method: 'PATCH'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        deleteCarcinogenicReportResponsible: builder.mutation({
            query: ({ reportId, responsibleId }: { reportId: number; responsibleId: number }) => ({
                url: `${BASE_URL}/${reportId}/responsables/${responsibleId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        }),
        vincularCarcinogenicReport: builder.mutation({
            query: (idInforme: number) => ({
                url: `${BASE_URL}/${idInforme}/vincular`,
                method: 'POST'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['CarcinogenicReport'])
        })
    })
});

export const {
    useCreateCarcinogenicReportMutation,
    useEditCarcinogenicReportEstablishmentMutation,
    useGetCarcinogenicParametricDataQuery,
    useGetCarcinogenicReportQuery,
    useGetSubstancesQuery,
    useEditCarcinogenicSubstanceMutation,
    useCreateCarcinogenicSubstanceStudyMutation,
    useDeleteCarcinogenicSubstanceStudyMutation,
    useCreateCarcinogenicProviderMutation,
    useEditCarcinogenicProviderMutation,
    useDeleteCarcinogenicProviderMutation,
    useCreateRadiologicalEquipmentMutation,
    useEditCarcinogenicEquipmentMutation,
    useDeleteCarcinogenicEquipmentMutation,
    useDeleteCarcinogenicEquipmentResponsibleMutation,
    useGetCarcinogenicReportWorkersQuery,
    useGetCarcinogenicReportWorkerDetailQuery,
    useLazyGetCarcinogenicReportWorkerDetailQuery,
    useEditCarcinogenicReportWorkerDetailMutation,
    useAddCarcinogenicReportContractorMutation,
    useEditCarcinogenicReportContractorMutation,
    useDeleteCarcinogenicContractorMutation,
    useAddCarcinogenicReportResponsibleMutation,
    useEditCarcinogenicReportResponsibleMutation,
    useDeleteCarcinogenicReportResponsibleMutation,
    useVincularCarcinogenicReportMutation
} = carcinogenicApi;
